import { useContext, useState } from "react";
import { Box } from "@mui/material";
import { FEATURES } from "@home-health-notify/domain";
import { OrganizationContext } from "hhn-commons";
import WorkspaceLayout from "../layout/WorkspaceLayout";
import { SyncQueueContent } from "../syncQueue/SyncQueueContent";
import EmrSetupForm from "../syncQueue/EmrSetupForm";
import { ExportChat } from "../exportChats/ExportChat";
import { LabRequisitionsListing } from "../labRequisitions/LabRequisitionsListing";
import { AutoAckContent } from "../autoAck/AutoAckContent";
import { OrgGeneralSettingsContent } from "../orgSettings/OrgGeneralSettingsContent";
import { GroupHead, NavItem, SubNav } from "./MoreArea.style";

type InlineKey =
  | "emr-patients"
  | "emr-setup"
  | "lab"
  | "export"
  | "auto-ack"
  | "org-settings";

const CRUMB: Record<InlineKey, string[]> = {
  "emr-patients": ["EMR", "Patients"],
  "emr-setup": ["EMR", "Setup"],
  lab: ["Tools", "Lab Requisitions"],
  export: ["Tools", "Export Chat"],
  "auto-ack": ["Organization", "Auto ask acknowledgement"],
  "org-settings": ["Organization", "Working hours & reminders"],
};

export function MoreArea({ closeModal }: { closeModal: () => void }) {
  const { selectedOrgUser } = useContext(OrganizationContext);

  // Gate on the backend feature flag only — a disabled org must not render the
  // EMR screens (and so must never call the sync-queue API, which 403s when off).
  const emrSyncEnabled = !!selectedOrgUser?.isEmrSyncEnabled();
  const canEmr =
    emrSyncEnabled && !!selectedOrgUser?.hasAccess(FEATURES.MANAGE_SYNC_QUEUE);
  const canEmrSetup =
    emrSyncEnabled &&
    !!selectedOrgUser?.hasAccess(FEATURES.MANAGE_SYNC_SETTINGS);
  const canLab =
    !!selectedOrgUser?.isLabIntegrationEnabled() &&
    !!selectedOrgUser?.hasAccess(FEATURES.ACCESS_LAB_REQUISITIONS);
  const canExport = !!selectedOrgUser?.hasAccess(FEATURES.EXPORT_CHAT);
  const canAutoAck =
    !!selectedOrgUser?.isAutoAckEnabled() &&
    !!selectedOrgUser?.hasAccess(FEATURES.MANAGE_AUTO_ACK);
  const canOrgSettings = !!selectedOrgUser?.hasAccess(
    FEATURES.MANAGE_ORG_SETTINGS,
  );

  const defaultKey: InlineKey = canEmr
    ? "emr-patients"
    : canEmrSetup
      ? "emr-setup"
      : canLab
        ? "lab"
        : canExport
          ? "export"
          : canAutoAck
            ? "auto-ack"
            : "org-settings";
  const [selected, setSelected] = useState<InlineKey>(defaultKey);

  const sideNav = (
    <SubNav data-testid="more-subnav">
      {(canEmr || canEmrSetup) && (
        <>
          <GroupHead>EMR</GroupHead>
          {canEmr && (
            <NavItem
              active={selected === "emr-patients"}
              onClick={() => setSelected("emr-patients")}
              data-testid="more-emr-patients"
            >
              Patients
            </NavItem>
          )}
          {canEmrSetup && (
            <NavItem
              active={selected === "emr-setup"}
              onClick={() => setSelected("emr-setup")}
              data-testid="more-emr-setup"
            >
              Setup
            </NavItem>
          )}
        </>
      )}

      {(canLab || canExport) && (
        <>
          <GroupHead>Tools</GroupHead>
          {canLab && (
            <NavItem
              active={selected === "lab"}
              onClick={() => setSelected("lab")}
            >
              Lab Requisitions
            </NavItem>
          )}
          {canExport && (
            <NavItem
              active={selected === "export"}
              onClick={() => setSelected("export")}
            >
              Export Chat
            </NavItem>
          )}
        </>
      )}

      {(canAutoAck || canOrgSettings) && (
        <>
          <GroupHead>Organization</GroupHead>
          {canAutoAck && (
            <NavItem
              active={selected === "auto-ack"}
              onClick={() => setSelected("auto-ack")}
              data-testid="more-auto-ack"
            >
              Auto ask acknowledgement
            </NavItem>
          )}
          {canOrgSettings && (
            <NavItem
              active={selected === "org-settings"}
              onClick={() => setSelected("org-settings")}
              data-testid="more-org-settings"
            >
              Working hours & reminders
            </NavItem>
          )}
        </>
      )}
    </SubNav>
  );

  return (
    <WorkspaceLayout
      path={["More", ...CRUMB[selected]]}
      onClose={closeModal}
      sideNav={sideNav}
    >
      {selected === "emr-patients" && canEmr && <SyncQueueContent embedded />}
      {selected === "emr-setup" && canEmrSetup && (
        <Box sx={{ p: 3 }}>
          <EmrSetupForm />
        </Box>
      )}
      {selected === "lab" && canLab && <LabRequisitionsListing />}
      {selected === "export" && canExport && <ExportChat />}
      {selected === "auto-ack" && canAutoAck && <AutoAckContent />}
      {selected === "org-settings" && canOrgSettings && (
        <OrgGeneralSettingsContent />
      )}
    </WorkspaceLayout>
  );
}
